import { debounce } from 'lodash';
import { snapshot } from 'valtio';
import { calcDateRange } from '../../app/store-actions/top-bar-store-actions';
import TopBarStore from '../../app/stores/top-bar-store';
import { createSimpleAsyncAction } from 'async-utils';
import { getAppOwnersStore } from '../../application-owners/api/applications-owners-api';
import { AppEvents } from 'common-events';
import {
  AppPages,
  getQueryString,
  navigate,
  navigateToArtifacts,
  navigateWithFilters,
} from '../../common/routing/app-navigator';
import { loadDashboardInfo } from '../../executive-summary/actions/executive-summary-actions';
import { trackAppcuesEvent } from '../../telemetry/appcues';
import artifactsService from '../services';
import {
  setArtifactModal,
  setArtifacts,
  setArtifactsFiltersList,
  setArtifactsOffset,
  setArtifactsTopFiltersList,
  setArtifactsTotalNumber,
  setLoadingSingleartifact,
  setSearchValues,
  setSelectedArtifact,
  setSelectedArtifactId,
  setStoreArtifactsFiltersTypeSearch,
  setSupplyChainItems,
  setTotalFilterdArtifacts,
} from '../store-actions/artifacts-store-actions';
import ArtifactsStore from '../stores/artifacts-store';
import {
  ArtifactTopFiltersTypes,
  LoadArtifactsParams,
} from '../types/artifacts-type';
import { OneOfValues } from 'common-types';
import { loadConnectorsByFamily } from '../../connectors/api/connectors-api';
import { createConnectorsPanelData } from '../../supply-chain/utils/supply-chain-connectors-utils';
import { createSupplyChainInfo } from '../../supply-chain/utils/supply-chain-utils';
import {
  FilterElements,
  FilterTypeSearch,
} from '../../common/filters-utils/filters-utils';

export const loadArtifacts = createSimpleAsyncAction(
  async (params?: LoadArtifactsParams) => {
    const { update = false, limit = 100 } = params || {};
    const { dateRange } = snapshot(TopBarStore);
    const [from, to] = calcDateRange(dateRange);
    const { displaySelectedAppOwners } = getAppOwnersStore();

    if (update) {
      ArtifactsStore.offset = 0;
    }
    const { offset, total, filterArtifactsBy, topFiltersArtifactsBy } =
      snapshot(ArtifactsStore);
    if (total < offset) return;

    const response = await artifactsService.getArtifacts.execute({
      offset: offset,
      limit,
      artifactTopFilters: topFiltersArtifactsBy,
      filters: filterArtifactsBy,
      dateRange: {
        from,
        to,
      },
      owners: displaySelectedAppOwners,
    });

    if (response) {
      const { artifacts, offset, total, totalFilteredArtifacts } = response;
      loadDashboardInfo();
      setArtifacts(artifacts, update);
      setArtifactsTotalNumber(total);
      setArtifactsOffset(offset);
      setTotalFilterdArtifacts(totalFilteredArtifacts);
    }
  },
  {
    asyncState: ArtifactsStore.loading,
    errorMessage: 'Failed to load artifacts',
  },
);

export const loadArtifactsFilters = createSimpleAsyncAction(
  async (params?: LoadArtifactsParams) => {
    const { update = false, limit = 100 } = params || {};
    const { dateRange } = snapshot(TopBarStore);
    const [from, to] = calcDateRange(dateRange);
    const { displaySelectedAppOwners } = getAppOwnersStore();

    if (update) {
      ArtifactsStore.offset = 0;
    }
    const { offset, total, filterArtifactsBy, topFiltersArtifactsBy } =
      snapshot(ArtifactsStore);
    if (total < offset) return;

    const response = await artifactsService.getArtifactsFilters.execute({
      offset: offset,
      limit,
      filters: filterArtifactsBy,
      artifactTopFilters: topFiltersArtifactsBy,
      dateRange: {
        from,
        to,
      },
      owners: displaySelectedAppOwners,
    });

    if (response) {
      setStoreArtifactsFiltersTypeSearch(response);
      setArtifactsFiltersList(response);
    }
  },
  {
    asyncState: ArtifactsStore.loadingFilters,
    errorMessage: 'Failed to load artifacts filters',
  },
);

export const loadArtifactsTopFilters = createSimpleAsyncAction(
  async (params?: LoadArtifactsParams) => {
    const { limit = 100 } = params || {};
    const { dateRange } = snapshot(TopBarStore);
    const [from, to] = calcDateRange(dateRange);
    const { displaySelectedAppOwners } = getAppOwnersStore();

    const { offset, total, topFiltersArtifactsBy, filterArtifactsBy } =
      snapshot(ArtifactsStore);
    if (total < offset) return;
    const response = await artifactsService.getArtifactsTopFilters.execute({
      offset: offset,
      limit,
      artifactTopFilters: topFiltersArtifactsBy,
      filters: filterArtifactsBy,
      dateRange: {
        from,
        to,
      },
      owners: displaySelectedAppOwners,
    });

    if (response) {
      setArtifactsTopFiltersList(response);
    }
  },
  {
    asyncState: ArtifactsStore.loadingFilters,
    errorMessage: 'Failed to load top artifacts filters',
  },
);

export const initArtifacts = async () => {
  const { offset, filtersArtifacts } = snapshot(ArtifactsStore);
  const { dateRange } = snapshot(TopBarStore);
  const [from, to] = calcDateRange(dateRange);
  const { displaySelectedAppOwners } = getAppOwnersStore();

  const results = await artifactsService.getArtifacts.execute({
    offset,
    limit: 1,
    filters: filtersArtifacts,
    dateRange: {
      from,
      to,
    },
    owners: displaySelectedAppOwners,
  });

  if (results && results.artifacts.length > 0) {
    const qs = getQueryString();
    const urlIssueId = qs.get('id');
    if (!urlIssueId) {
      setArtifactDrawer(results.artifacts[0].id);
    } else {
      setArtifactDrawer(urlIssueId);
    }
  }
};

export const setArtifactDrawer = async (artifactId: string): Promise<void> => {
  if (artifactId === ArtifactsStore.selectedArtifactId) return;
  setSelectedArtifactId(artifactId);
  setLoadingSingleartifact(true);
  const result = await artifactsService.getSingleArtifact.execute({
    artifactId,
  });
  if (result) {
    trackAppcuesEvent(AppEvents.Artifacts.ArtifactSelected);
    setSelectedArtifact(result);
    setSelectedArtifactId(result.id);
    const connectorsByFamily = await loadConnectorsByFamily();
    const { connectorsPanelData } =
      createConnectorsPanelData(connectorsByFamily);

    const supplyChainItems = createSupplyChainInfo(
      result.supplyChainInfo,
      connectorsPanelData,
    );
    setSupplyChainItems(supplyChainItems);
    setArtifactModal(true);
  }
  setLoadingSingleartifact(false);
};

export const handleArtifactRowClick = debounce(
  (artifactId: string, pageUrl: OneOfValues<typeof AppPages>) => {
    navigate(pageUrl, `?id=${artifactId}`);
    setArtifactDrawer(artifactId);
  },
  500,
);

export const closeArtifactModal = () => {
  setArtifactModal(false);
  setSelectedArtifact(null);
  setSelectedArtifactId('');
};

export const setSelectedArtifactFilter = async () => {
  await loadArtifacts({ update: true });
  const { artifacts, selectedArtifactId } = snapshot(ArtifactsStore);
  const selectedIssueAfterFilter = artifacts.find(
    artifact => artifact.id === selectedArtifactId,
  );
  if (selectedIssueAfterFilter === undefined) {
    closeArtifactModal();
  }
};

export const onChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (ArtifactsStore.filterArtifactsBy[e.target.id]) {
    const index = ArtifactsStore.filterArtifactsBy[e.target.id].indexOf(
      e.target.value,
    );
    if (index !== -1) {
      ArtifactsStore.filterArtifactsBy[e.target.id].splice(index, 1);
    } else {
      ArtifactsStore.filterArtifactsBy[e.target.id].push(e.target.value);
    }
  } else {
    ArtifactsStore.filterArtifactsBy[e.target.id] = [e.target.value];
  }
  navigateToArtifacts(ArtifactsStore.filterArtifactsBy);
  if (ArtifactsStore.selectedArtifactId) {
    setSelectedArtifactFilter();
  } else {
    loadArtifacts({ update: true });
  }
  loadArtifactsFilters();
};

export const onChangeTopFilter = (filterType: ArtifactTopFiltersTypes) => {
  if (ArtifactsStore.topFiltersArtifactsBy.includes(filterType)) {
    const index = ArtifactsStore.topFiltersArtifactsBy.indexOf(filterType);
    if (index !== -1) {
      ArtifactsStore.topFiltersArtifactsBy.splice(index, 1);
    } else {
      ArtifactsStore.topFiltersArtifactsBy.push(filterType);
    }
  } else {
    ArtifactsStore.topFiltersArtifactsBy.push(filterType);
  }
  // navigateToArtifacts(ArtifactsStore.topFiltersArtifactsBy);
  loadArtifacts({ update: true });
  loadArtifactsFilters();
  loadArtifactsTopFilters();
};

export const clearFilters = () => {
  ArtifactsStore.filterArtifactsBy = {};
  ArtifactsStore.numberOfFilters = 0;
  navigate(AppPages.Artifacts);
  loadArtifacts({ update: true });
};

export const clearFilter = (type: string) => {
  ArtifactsStore.filterArtifactsBy[type] = [];
  navigateWithFilters(
    AppPages.Artifacts,
    snapshot(ArtifactsStore.filterArtifactsBy),
  );
  loadArtifacts({ update: true });
  loadArtifactsFilters();
};

export const setInitialFilters = (encodedFilters: string | null) => {
  if (!encodedFilters) return;
  let filters = {};
  try {
    filters = JSON.parse(decodeURI(encodedFilters));
  } catch {}
  ArtifactsStore.filterArtifactsBy = filters;
};

export const handleSearchFilterType = (
  searchInput: string,
  filterType: string,
) => {
  const { artifactsFiltersList } = snapshot(ArtifactsStore);
  if (artifactsFiltersList && filterType) {
    const newFilteredTypes = artifactsFiltersList[filterType].filter(
      (artifactFilterType: FilterElements) =>
        artifactFilterType.label
          .toLocaleLowerCase()
          .includes(searchInput.toLowerCase()),
    );
    setSearchValues(filterType, searchInput);
    setStoreArtifactsFiltersTypeSearch({
      ...artifactsFiltersList,
      [FilterTypeSearch[filterType]]: newFilteredTypes,
    });
  }
};
